import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { __ } from '@wordpress/i18n';
import Widgets from './Widgets';
import iziToast from 'izitoast';

const VersionControlContent = () => {
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);

  const [proVersions, setProVersions] = useState([]);
  const [selectedProVersion, setSelectedProVersion] = useState('');
  const [proLoading, setProLoading] = useState(false);
  const [proRollingBack, setProRollingBack] = useState(false);

  useEffect(() => {
    setLoading(true);
    const formData = new FormData();
    formData.append('action', 'thha_get_rollback_versions');
    formData.append('nonce', window.thhaAdminSettings.nonce);

    fetch(window.thhaAdminSettings.ajaxurl, {
      method: 'POST',
      body: formData,
    })
      .then(res => res.json())
      .then(response => {
        if (response.success && Array.isArray(response.data)) {
          setVersions(response.data);
          if (response.data.length > 0) {
            setSelectedVersion(response.data[0]);
          }
        }
      })
      .catch(error => {
        console.error('Error fetching rollback versions:', error);
      })
      .finally(() => {
        setLoading(false);
      });

    // Fetch Pro rollback versions if Pro plugin is active
    if (window.thhaAdminSettings.is_pro_active) {
      setProLoading(true);
      const proFormData = new FormData();
      proFormData.append('action', 'thha_get_pro_rollback_versions');
      proFormData.append('nonce', window.thhaAdminSettings.nonce);

      fetch(window.thhaAdminSettings.ajaxurl, {
        method: 'POST',
        body: proFormData,
      })
        .then(res => res.json())
        .then(response => {
          if (response.success && Array.isArray(response.data)) {
            setProVersions(response.data);
            if (response.data.length > 0) {
              setSelectedProVersion(response.data[0]);
            }
          }
        })
        .catch(error => {
          console.error('Error fetching pro rollback versions:', error);
        })
        .finally(() => {
          setProLoading(false);
        });
    }
  }, []);

  const handleRollback = () => {
    if (!selectedVersion) return;

    const confirmRollback = window.confirm(
      `Are you sure you want to rollback Huge Addons for Elementor to version ${selectedVersion}? This will overwrite your current plugin files.`
    );
    if (!confirmRollback) return;

    setRollingBack(true);
    const formData = new FormData();
    formData.append('action', 'thha_rollback_version');
    formData.append('nonce', window.thhaAdminSettings.nonce);
    formData.append('version', selectedVersion);

    fetch(window.thhaAdminSettings.ajaxurl, {
      method: 'POST',
      body: formData,
    })
      .then(res => res.json())
      .then(response => {
        if (response.success) {
          iziToast.show({
            message: `Successfully rolled back to version ${selectedVersion}! Page reloading...`,
            position: 'topRight',
            backgroundColor: '#FFFFFF',
            messageColor: '#19192C',
            progressBarColor: '#22C55E',
          });
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          throw new Error(response.data?.message || 'Rollback failed.');
        }
      })
      .catch(error => {
        iziToast.error({
          message: error.message,
          position: 'topRight',
        });
        setRollingBack(false);
      });
  };

  const handleProRollback = () => {
    if (!selectedProVersion) return;

    const confirmRollback = window.confirm(
      `Are you sure you want to rollback Huge Addons for Elementor Pro to version ${selectedProVersion}? This will overwrite your current Pro plugin files.`
    );
    if (!confirmRollback) return;

    setProRollingBack(true);
    const formData = new FormData();
    formData.append('action', 'thha_rollback_pro_version');
    formData.append('nonce', window.thhaAdminSettings.nonce);
    formData.append('version', selectedProVersion);

    fetch(window.thhaAdminSettings.ajaxurl, {
      method: 'POST',
      body: formData,
    })
      .then(res => res.json())
      .then(response => {
        if (response.success) {
          iziToast.show({
            message: `Successfully rolled back Pro to version ${selectedProVersion}! Page reloading...`,
            position: 'topRight',
            backgroundColor: '#FFFFFF',
            messageColor: '#19192C',
            progressBarColor: '#22C55E',
          });
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          throw new Error(response.data?.message || 'Rollback failed.');
        }
      })
      .catch(error => {
        iziToast.error({
          message: error.message,
          position: 'topRight',
        });
        setProRollingBack(false);
      });
  };

  return (
    <div className="version-control-content">
      <h2>Version Control</h2>

      <div className="thha-card">
        {/* Rollback Section */}
        <div className="setting-block">
          <h3>Rollback to Previous Version</h3>
          <p>
            Experiencing an issue with Huge Addons for Elementor version{' '}
            <strong>{window.thhaAdminSettings.version}</strong>? Roll back to a previous version to help troubleshoot the issue.
          </p>
          <div className="control-row">
            {loading ? (
              <span>Loading versions...</span>
            ) : (
              <select 
                value={selectedVersion} 
                onChange={e => setSelectedVersion(e.target.value)} 
                className="version-select"
                disabled={rollingBack}
              >
                {versions.map(v => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            )}
            <button 
              className="rollback-button" 
              onClick={handleRollback}
              disabled={rollingBack || !selectedVersion || loading}
            >
              {rollingBack ? 'Rolling back...' : 'Rollback'}
            </button>
          </div>
        </div>

        {/* Pro Rollback Section */}
        {window.thhaAdminSettings.is_pro_active && (
          <div className="setting-block" style={{ borderTop: '1px solid #EAEAEA', paddingTop: '20px', marginTop: '20px' }}>
            <h3>Rollback to Previous Version (Pro)</h3>
            <p>
              Experiencing an issue with Huge Addons for Elementor Pro version{' '}
              <strong>{window.thhaAdminSettings.pro_version}</strong>? Roll back to a previous version to help troubleshoot the issue.
            </p>
            <div className="control-row">
              {proLoading ? (
                <span>Loading Pro versions...</span>
              ) : (
                <select 
                  value={selectedProVersion} 
                  onChange={e => setSelectedProVersion(e.target.value)} 
                  className="version-select"
                  disabled={proRollingBack}
                >
                  {proVersions.map(v => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              )}
              <button 
                className="rollback-button" 
                onClick={handleProRollback}
                disabled={proRollingBack || !selectedProVersion || proLoading}
              >
                {proRollingBack ? 'Rolling back...' : 'Rollback Pro'}
              </button>
            </div>
          </div>
        )}

        {/* Enable Beta Section */}
        <div className="setting-block with-toggle">
          <div className="text-content">
            <h3>Enable Beta</h3>
            <p>
              Enable this option to turn on beta updates and be notified when a new beta version is available.
              The beta version will not install automatically.
            </p>
          </div>
          <label className="switch">
            <input type="checkbox" />
            <span className="slider round"></span>
          </label>
        </div>

        {/* Enable Legacy Blocks Section */}
        <div className="setting-block with-toggle">
          <div className="text-content">
            <h3>Enable Legacy Blocks</h3>
            <p>Enable this option to enable the support of our Legacy Blocks on the site.</p>
          </div>
          <label className="switch">
            <input type="checkbox" defaultChecked />
            <span className="slider round"></span>
          </label>
        </div>
      </div>
    </div>
  );
};

// Placeholder for other sections
const PlaceholderContent = ({ title }) => {
  const upcomingImg = () => {
    return window.thhaAdminSettings.assets_url + 'img/upcoming.svg';
  };

  return (
    <div className="placeholder-coming-soon-wrapper">
      <div className="coming-soon-card">
        <div className="coming-soon-icon-wrap">
          <img src={upcomingImg()} className="coming-soon-image" alt="Coming Soon Illustration" />
        </div>
        <h2 className="coming-soon-title">{title} Settings</h2>
        <p className="coming-soon-desc">
          We are currently crafting some amazing features and customization options for <strong>{title}</strong>. This section will be available in the upcoming release. Stay tuned!
        </p>
        <div className="coming-soon-badge">Coming Soon</div>
      </div>
    </div>
  );
};

const IntegrationContent = () => {
  const [userId, setUserId] = useState(window.thhaAdminSettings.instagram_user_id || '');
  const [accessToken, setAccessToken] = useState(window.thhaAdminSettings.instagram_access_token || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = () => {
    setIsSaving(true);
    const formData = new FormData();
    formData.append('action', 'thha_save_instagram_settings');
    formData.append('_wpnonce', window.thhaAdminSettings.saveNonce);
    formData.append('instagram_user_id', userId);
    formData.append('instagram_access_token', accessToken);

    fetch(window.thhaAdminSettings.ajaxurl, {
      method: 'POST',
      body: formData,
    })
      .then(res => res.json())
      .then(response => {
        if (response.success) {
          iziToast.show({
            message: 'Instagram settings saved successfully!',
            position: 'topRight',
            backgroundColor: '#FFFFFF',
            messageColor: '#19192C',
            progressBarColor: '#22C55E',
          });
          window.thhaAdminSettings.instagram_user_id = userId;
          window.thhaAdminSettings.instagram_access_token = accessToken;
        } else {
          throw new Error(response.data || 'Failed to save settings');
        }
      })
      .catch(error => {
        iziToast.error({
          message: error.message,
          position: 'topRight',
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <div className="version-control-content">
      <h2>Integration Settings</h2>

      <div className="thha-card">
        <div className="setting-block" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          <h3>Instagram Integration</h3>
          <p style={{ marginBottom: '15px' }}>
            Configure your Instagram Graph API credentials to display your feed on the website.
          </p>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px' }}>Instagram User ID</label>
            <input 
              type="text" 
              value={userId} 
              onChange={e => setUserId(e.target.value)} 
              className="version-select"
              style={{ width: '100%', padding: '10px', height: '40px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }}
              placeholder="e.g. 17841400000000000"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px' }}>Access Token</label>
            <input 
              type="password" 
              value={accessToken} 
              onChange={e => setAccessToken(e.target.value)} 
              className="version-select"
              style={{ width: '100%', padding: '10px', height: '40px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' }}
              placeholder="Enter your Instagram access token"
            />
          </div>

          <div>
            <button 
              className="rollback-button" 
              onClick={handleSave} 
              disabled={isSaving}
              style={{ padding: '10px 20px', cursor: 'pointer' }}
            >
              {isSaving ? 'Saving...' : 'Save Credentials'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AssetGenerationContent = () => {
  const [jsPrintMethod, setJsPrintMethod] = useState(window.thhaAdminSettings.js_print_method || 'external');
  const [isSaving, setIsSaving] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const handleRegenerate = () => {
    setIsRegenerating(true);
    const formData = new FormData();
    formData.append('action', 'thha_regenerate_assets');
    formData.append('_wpnonce', window.thhaAdminSettings.saveNonce);

    fetch(window.thhaAdminSettings.ajaxurl, {
      method: 'POST',
      body: formData,
    })
      .then(res => res.json())
      .then(response => {
        if (response.success) {
          iziToast.show({
            message: 'Assets regenerated successfully!',
            position: 'topRight',
            backgroundColor: '#FFFFFF',
            messageColor: '#19192C',
            progressBarColor: '#22C55E',
          });
        } else {
          throw new Error(response.data || 'Failed to regenerate assets.');
        }
      })
      .catch(error => {
        iziToast.error({
          message: error.message,
          position: 'topRight',
        });
      })
      .finally(() => {
        setIsRegenerating(false);
      });
  };

  const handleSave = () => {
    setIsSaving(true);
    const formData = new FormData();
    formData.append('action', 'thha_save_asset_generation_settings');
    formData.append('_wpnonce', window.thhaAdminSettings.saveNonce);
    formData.append('js_print_method', jsPrintMethod);

    fetch(window.thhaAdminSettings.ajaxurl, {
      method: 'POST',
      body: formData,
    })
      .then(res => res.json())
      .then(response => {
        if (response.success) {
          iziToast.show({
            message: 'Settings saved successfully!',
            position: 'topRight',
            backgroundColor: '#FFFFFF',
            messageColor: '#19192C',
            progressBarColor: '#22C55E',
          });
          window.thhaAdminSettings.js_print_method = jsPrintMethod;
        } else {
          throw new Error(response.data || 'Failed to save settings.');
        }
      })
      .catch(error => {
        iziToast.error({
          message: error.message,
          position: 'topRight',
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <div className="asset-generation-content">
      <h2>{__('Asset Generation', 'huge-addons-for-elementor')}</h2>

      <div className="asset-generation-options">
        {/* Card 1: Regenerate Assets */}
        <div className="option-card">
          <div className="option-card-left">
            <div className="icon-wrapper blue-bg">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#007bff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
            </div>
            <div className="text-wrapper">
              <h3>{__('Regenerate Assets', 'huge-addons-for-elementor')}</h3>
              <p>{__('Huge Addons styles & scripts are saved in Uploads folder. This option will clear all those generated files.', 'huge-addons-for-elementor')}</p>
            </div>
          </div>
          <div className="option-card-right">
            <button 
              className="outline-action-btn"
              onClick={handleRegenerate}
              disabled={isRegenerating}
            >
              {isRegenerating ? __('Regenerating...', 'huge-addons-for-elementor') : __('Regenerate Assets', 'huge-addons-for-elementor')}
            </button>
          </div>
        </div>

        {/* Card 2: Assets Embed Method */}
        <div className="option-card">
          <div className="option-card-left">
            <div className="icon-wrapper blue-bg">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#007bff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </div>
            <div className="text-wrapper">
              <h3>{__('Assets Embed Method', 'huge-addons-for-elementor')}</h3>
              <p>{__('Configure the Huge Addons assets embed method. Keep it as default (recommended).', 'huge-addons-for-elementor')}</p>
            </div>
          </div>
          <div className="option-card-right">
            <a 
              href={window.thhaAdminSettings.adminPostUrl.replace('admin-post.php', 'admin.php?page=elementor-settings#tab-performance')}
              target="_blank"
              rel="noopener noreferrer"
              className="outline-action-btn-link"
            >
              {__('CSS Print Method', 'huge-addons-for-elementor')}
            </a>
          </div>
        </div>

        {/* Card 3: JS Print Method */}
        <div className="option-card column-layout">
          <div className="option-card-top">
            <div className="option-card-left">
              <div className="text-wrapper no-icon">
                <h3>{__('JS Print Method', 'huge-addons-for-elementor')}</h3>
              </div>
            </div>
            <div className="option-card-right">
              <div className="select-wrapper">
                <select 
                  value={jsPrintMethod} 
                  onChange={(e) => setJsPrintMethod(e.target.value)} 
                  className="action-select"
                >
                  <option value="external">{__('External file', 'huge-addons-for-elementor')}</option>
                  <option value="internal">{__('Internal Embedding', 'huge-addons-for-elementor')}</option>
                </select>
              </div>
            </div>
          </div>
          <div className="option-card-bottom">
            <p className="helper-text">
              {__('CSS Print Method is handled by Elementor Settings itself. Use External CSS Files for better performance (recommended).', 'huge-addons-for-elementor')}
            </p>
          </div>
        </div>
      </div>

      <div className="save-settings-bar">
        <button 
          className="save-settings-btn"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? __('Saving...', 'huge-addons-for-elementor') : __('Save Settings', 'huge-addons-for-elementor')}
        </button>
      </div>
    </div>
  );
};

const PerformanceContent = () => {
  const [lazyLoad, setLazyLoad] = useState(window.thhaAdminSettings.lazy_load === '1');
  const [wipeSettings, setWipeSettings] = useState(window.thhaAdminSettings.wipe_settings === '1');
  const [enableCustomCode, setEnableCustomCode] = useState(window.thhaAdminSettings.enable_custom_code === '1');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = () => {
    setIsSaving(true);
    const formData = new FormData();
    formData.append('action', 'thha_save_performance_settings');
    formData.append('_wpnonce', window.thhaAdminSettings.saveNonce);
    formData.append('lazy_load', lazyLoad ? '1' : '0');
    formData.append('wipe_settings', wipeSettings ? '1' : '0');
    formData.append('enable_custom_code', enableCustomCode ? '1' : '0');

    fetch(window.thhaAdminSettings.ajaxurl, {
      method: 'POST',
      body: formData,
    })
      .then(res => res.json())
      .then(response => {
        if (response.success) {
          iziToast.show({
            message: 'Performance settings saved successfully!',
            position: 'topRight',
            backgroundColor: '#FFFFFF',
            messageColor: '#19192C',
            progressBarColor: '#22C55E',
          });
          window.thhaAdminSettings.lazy_load = lazyLoad ? '1' : '0';
          window.thhaAdminSettings.wipe_settings = wipeSettings ? '1' : '0';
          window.thhaAdminSettings.enable_custom_code = enableCustomCode ? '1' : '0';
        } else {
          throw new Error(response.data || 'Failed to save settings.');
        }
      })
      .catch(error => {
        iziToast.error({
          message: error.message,
          position: 'topRight',
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <div className="performance-content">
      <div className="performance-header-wrap">
        <h2>{__('Performance', 'huge-addons-for-elementor')}</h2>
        <span className="features-badge">{__('03 Features', 'huge-addons-for-elementor')}</span>
      </div>

      <div className="performance-grid">
        {/* Card 1: Lazy Load */}
        <div className="performance-card">
          <div className="card-header-row">
            <h3>{__('Lazy Load', 'huge-addons-for-elementor')}</h3>
            <div className="card-controls">
              <button type="button" className="gear-config-btn" title={__('Configure', 'huge-addons-for-elementor')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
              <label className="switch">
                <input 
                  type="checkbox" 
                  checked={lazyLoad} 
                  onChange={(e) => setLazyLoad(e.target.checked)} 
                />
                <span className="slider round"></span>
              </label>
            </div>
          </div>
          <p className="card-description">
            {__("We are adding Lazy Load Functionality in each of our widgets. It's in beta version at this moment but completely implemented very soon. It will help you drastically in Google Web Vitals.", 'huge-addons-for-elementor')}
          </p>
        </div>

        {/* Card 2: Wipe Settings before Uninstall */}
        <div className="performance-card">
          <div className="card-header-row">
            <h3>{__('Wipe Settings before Uninstall', 'huge-addons-for-elementor')}</h3>
            <div className="card-controls">
              <button type="button" className="gear-config-btn" title={__('Configure', 'huge-addons-for-elementor')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
              <label className="switch">
                <input 
                  type="checkbox" 
                  checked={wipeSettings} 
                  onChange={(e) => setWipeSettings(e.target.checked)} 
                />
                <span className="slider round"></span>
              </label>
            </div>
          </div>
          <p className="card-description">
            {__("Note: Select the data you wish to delete from your database while uninstalling plugin. You'll need to reconfigure settings manually if you reinstall the plugin later. Once deleted, this data cannot be recovered.", 'huge-addons-for-elementor')}
          </p>
        </div>

        {/* Card 3: Custom Code Snippets */}
        <div className="performance-card">
          <div className="card-header-row">
            <h3>{__('Custom Code Snippets', 'huge-addons-for-elementor')}</h3>
            <div className="card-controls">
              <button type="button" className="gear-config-btn" title={__('Configure', 'huge-addons-for-elementor')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
              <label className="switch">
                <input 
                  type="checkbox" 
                  checked={enableCustomCode} 
                  onChange={(e) => setEnableCustomCode(e.target.checked)} 
                />
                <span className="slider round"></span>
              </label>
            </div>
          </div>
          <p className="card-description">
            {__("Enable this option to turn on the Custom Code Snippets manager. When enabled, you can add and run custom HTML, CSS, JavaScript, or PHP tracking codes and integration widgets directly from the dashboard.", 'huge-addons-for-elementor')}
          </p>
        </div>
      </div>

      <div className="save-settings-bar">
        <button 
          className="save-settings-btn"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? __('Saving...', 'huge-addons-for-elementor') : __('Save Settings', 'huge-addons-for-elementor')}
        </button>
      </div>
    </div>
  );
};

const LicenseActivationContent = () => {
  const [licenseKey, setLicenseKey] = useState('');
  const [status, setStatus] = useState('inactive'); // active, inactive, loading
  const [details, setDetails] = useState(null);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch current license info on mount
  useEffect(() => {
    fetch(`${window.thhaAdminSettings.ajaxurl}?action=thha_pro_get_license_info&nonce=${window.thhaAdminSettings.nonce}`)
      .then(res => res.json())
      .then(res => {
        if (res.success && res.data && res.data.active) {
          setStatus('active');
          setDetails(res.data);
          setLicenseKey(res.data.license_key);
        } else {
          setStatus('inactive');
        }
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const handleActivate = (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setIsError(false);

    const formData = new FormData();
    formData.append('action', 'thha_pro_activate_license');
    formData.append('nonce', window.thhaAdminSettings.nonce);
    formData.append('license_key', licenseKey);

    fetch(window.thhaAdminSettings.ajaxurl, {
      method: 'POST',
      body: formData
    })
      .then(res => res.json())
      .then(res => {
        if (res.success) {
          setStatus('active');
          setDetails(res.data);
          setMessage(__('License activated successfully!', 'huge-addons-for-elementor-pro'));
          setIsError(false);
        } else {
          setMessage(res.data.message || __('Failed to activate license.', 'huge-addons-for-elementor-pro'));
          setIsError(true);
        }
      })
      .catch(err => {
        setMessage(__('Connection error.', 'huge-addons-for-elementor-pro'));
        setIsError(true);
      })
      .finally(() => setLoading(false));
  };

  const handleDeactivate = () => {
    if (!confirm(__('Are you sure you want to deactivate this license?', 'huge-addons-for-elementor-pro'))) {
      return;
    }
    setLoading(true);
    setMessage('');

    const formData = new FormData();
    formData.append('action', 'thha_pro_deactivate_license');
    formData.append('nonce', window.thhaAdminSettings.nonce);

    fetch(window.thhaAdminSettings.ajaxurl, {
      method: 'POST',
      body: formData
    })
      .then(res => res.json())
      .then(res => {
        if (res.success) {
          setStatus('inactive');
          setDetails(null);
          setLicenseKey('');
          setMessage(__('License deactivated successfully.', 'huge-addons-for-elementor-pro'));
          setIsError(false);
        } else {
          setMessage(res.data.message || __('Failed to deactivate license.', 'huge-addons-for-elementor-pro'));
          setIsError(true);
        }
      })
      .catch(err => {
        setMessage(__('Connection error.', 'huge-addons-for-elementor-pro'));
        setIsError(true);
      })
      .finally(() => setLoading(false));
  };

  if (loading && !details) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>{__('Loading license details...', 'huge-addons-for-elementor-pro')}</p>
      </div>
    );
  }

  return (
    <div className="license-settings-tab" style={{ padding: '20px' }}>
      <h2 style={{ fontSize: '22px', fontWeight: 'bold', margin: '0 0 10px 0' }}>{__('License Management', 'huge-addons-for-elementor-pro')}</h2>
      <p className="card-description" style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 20px 0' }}>
        {__('Configure your Huge Addons PRO license key to activate premium Elementor widgets and enable automated plugin updates.', 'huge-addons-for-elementor-pro')}
      </p>

      {message && (
        <div className={`notice notice-${isError ? 'error' : 'success'}`} style={{ margin: '15px 0', padding: '12px', borderRadius: '4px', borderLeft: '4px solid', borderColor: isError ? '#dc3232' : '#46b450', backgroundColor: '#fff', boxShadow: '0 1px 15px rgba(0,0,0,.05)' }}>
          <p style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: isError ? '#dc3232' : '#46b450' }}>{message}</p>
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '28px', boxShadow: '0 1px 15px rgba(0,0,0,.02)', marginTop: '20px' }}>
        {status === 'active' && details ? (
          <div>
            <h3 style={{ color: '#10b981', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', fontSize: '18px' }}>
              <span className="dashicons dashicons-yes-alt" style={{ fontSize: '24px', marginRight: '8px', color: '#10b981' }}></span>
              {__('License Status: Active', 'huge-addons-for-elementor-pro')}
            </h3>
            
            <div style={{ marginBottom: '28px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '14px 0', fontWeight: '600', width: '180px', color: '#374151' }}>{__('License Key', 'huge-addons-for-elementor-pro')}</td>
                    <td style={{ padding: '14px 0' }}>
                      <code style={{ background: '#f3f4f6', padding: '4px 8px', borderRadius: '4px', fontSize: '13px', color: '#111827' }}>
                        {licenseKey ? `${licenseKey.substring(0, 6)}••••••••••••••••••••${licenseKey.substring(licenseKey.length - 4)}` : ''}
                      </code>
                    </td>
                  </tr>
                  {details.expiration_date && (
                    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '14px 0', fontWeight: '600', color: '#374151' }}>{__('Expires On', 'huge-addons-for-elementor-pro')}</td>
                      <td style={{ padding: '14px 0', color: '#4b5563' }}>{details.expiration_date}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <button 
              type="button" 
              className="save-settings-btn"
              style={{ background: '#ef4444', borderColor: '#ef4444', color: '#white', cursor: 'pointer' }}
              onClick={handleDeactivate}
              disabled={loading}
            >
              {loading ? __('Processing...', 'huge-addons-for-elementor-pro') : __('Deactivate License', 'huge-addons-for-elementor-pro')}
            </button>
          </div>
        ) : (
          <div>
            <h3 style={{ color: '#ef4444', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', fontSize: '18px' }}>
              <span className="dashicons dashicons-warning" style={{ fontSize: '24px', marginRight: '8px', color: '#ef4444' }}></span>
              {__('License Status: Inactive', 'huge-addons-for-elementor-pro')}
            </h3>
            <p style={{ margin: '0 0 24px 0', color: '#4b5563', fontSize: '14px' }}>{__('Please enter your license key to register this website.', 'huge-addons-for-elementor-pro')}</p>

            <form onSubmit={handleActivate}>
              <div style={{ marginBottom: '24px' }}>
                <label htmlFor="license_key_input" style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>{__('License Key', 'huge-addons-for-elementor-pro')}</label>
                <input 
                  type="text" 
                  id="license_key_input" 
                  style={{ width: '100%', maxWidth: '450px', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }} 
                  value={licenseKey}
                  onChange={(e) => setLicenseKey(e.target.value)}
                  placeholder="ha_..."
                  required
                  disabled={loading}
                />
              </div>

              <button 
                type="submit" 
                className="save-settings-btn"
                style={{ cursor: 'pointer' }}
                disabled={loading}
              >
                {loading ? __('Activating...', 'huge-addons-for-elementor-pro') : __('Activate License', 'huge-addons-for-elementor-pro')}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

const renderContent = (activeTab) => {
	switch (activeTab) {
		case 'version-control':
			return <VersionControlContent />;
		case 'integration':
			return <IntegrationContent />;
		case 'asset-generation':
			return <AssetGenerationContent />;
		case 'performance':
			return <PerformanceContent />;
		case 'license-activation':
			return <LicenseActivationContent />;
		default:
			return <PlaceholderContent title={activeTab.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} />;
	}
};

const SettingsTab = () => {
	const [activeTab, setActiveTab] = useState(() => {
		const params = new URLSearchParams(window.location.search);
		return params.get('sub_tab') || 'editor-option';
	});

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		params.set('sub_tab', activeTab);
		const newUrl = `${window.location.pathname}?${params.toString()}`;
		window.history.replaceState({}, '', newUrl);
	}, [activeTab]);

	return (
		<div className="settings-container">
			<Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
			<div className="main-content-area">{renderContent(activeTab)}</div>
		</div>
	);
};

export default SettingsTab;